import { useState } from 'react';
import { ViewId } from '~/shared/utils';
import { useIntl } from 'react-intl';
import { CaptionButton } from '~/shared/components/ui/Button';
import { GetMessageWithIntl } from '~/shared/components';
import { getGenerallyIconElement } from '~/shared/components/parts/Button/GenerallyIcons';

import { Textarea } from '~/shared/components/ui';

interface AiAssistEditPanelItemProps {
  companyInfo?: string;
  evaluationIndex1?: string;
  evaluationIndex2?: string;
  evaluationIndex3?: string;
  evaluationIndex4?: string;
  prompt?: string;
  promptRunCount?: number;
  onPromptChange?: (value: string) => void;
  onGenerate: (prompt: string) => void;
  onPreviewGenerateHistory: () => void;
}

interface AiAssistEditPanelFooterProps {
  onBack: () => void;
  onTempSave: () => void;
}

const VIEW_ID: ViewId = 'OCP_PR_INPUT';
export const AiAssistEditPanelItem = ({
  companyInfo,
  evaluationIndex1,
  evaluationIndex2,
  evaluationIndex3,
  evaluationIndex4,
  prompt = '',
  promptRunCount = 0,
  onPromptChange,
  onGenerate,
  onPreviewGenerateHistory,
}: AiAssistEditPanelItemProps) => {
  const intl = useIntl();

  const handleGenerate = (cnt: number) => {
    onGenerate(prompt);
  };
  const handleGenerateHistory = () => {
    onPreviewGenerateHistory();
  };
  return (
    <>
      <div className="tab-info-body">
        <div className="description-area">
          <p className="title">
            {getGenerallyIconElement('unchecked')}
            AIアシスト編集
          </p>
          <p className="text-box">
            <span className="text">
              AI生成機能を活用して、サンプル文章を調整します。
              <br />
              この画面では公開登録は行いません。一時保存を行い、次の編集画面で最終調整を行います。
              <br />
              生成結果は生成履歴ボタンから確認可能です。
            </span>
          </p>
          <p className="text-box">
            <span className="text-red">
              ※生成結果には最新の自社企業情報より自動生成したサンプル文章を表示します。
            </span>
          </p>
          <p className="attention-text">
            {getGenerallyIconElement('lightbulb')}
            修正したい箇所や「もっとこうしてほしい」といった要望を入力して「生成する」ボタンをクリックしてください。
          </p>
          <p className="text-box">
            <span className="text" style={{ paddingLeft: '1rem' }}>
              （例：「ステンレス加工の加工精度が強みなのでアピールしたい」「創業30年を追記」など。※指示は簡潔に伝えると、より正確に反映されます）
            </span>
          </p>
        </div>
        <div className="input-line">
          <div className="item-group-100">
            <div className="w-60">
              <Textarea
                name="prompt"
                className="w-100 mh-middle"
                value={prompt}
                labelId={`${VIEW_ID}.prompt`}
                columns={['prompt']}
                onChangeState={(v) => onPromptChange?.(v)}
              />
            </div>
          </div>
        </div>

        <div className="dashboad-container-area">
          <div className="dashboad-status-section">
            <div className="dashboad-status-labels">
              <span className="dashboad-label-title">本日の試行回数</span>
              <span className="dashboad-label-count">
                {promptRunCount ?? 0} / 20
              </span>
            </div>
            <div className="dashboad-progress-bar">
              <div
                className="dashboad-progress-fill"
                style={{ width: ((promptRunCount ?? 0) / 20) * 100 + '%' }}
              ></div>
            </div>
          </div>

          <div className="dashboad-button-group">
            <CaptionButton
              name="resetBtn"
              caption={GetMessageWithIntl(intl, {
                prefixId: VIEW_ID,
                id: 'generateBtn',
              })}
              className="btn"
              onClick={() => handleGenerate(promptRunCount ?? 0)}
              buttonType="basic"
            />
            <CaptionButton
              name="resetBtn"
              caption={GetMessageWithIntl(intl, {
                prefixId: VIEW_ID,
                id: 'generateHistoryBtn',
              })}
              className="btn"
              onClick={handleGenerateHistory}
              buttonType="basic"
            />
          </div>
        </div>
        <hr />
        <div className="description-area">
          <p className="title">生成結果</p>
          <p className="text-box">
            <span className="text">
              生成されたサンプル文章を確認してください。
              <br />
              手動で修正が必要な場合は、次の編集画面で最終調整を行ってください。
              <br />
              もう一度AI生成機能を活用して、サンプル文章を修正する場合は、指示文言を修正し、「生成する」ボタンをクリックしてください。
              <br />
              ※現在の公開情報、生成結果を確認する場合は、上の「生成履歴」ボタンをクリックしてください。
              <br />
            </span>
            <span className="text" style={{ paddingLeft: '1rem' }}>
              指定バージョンを今回の更新内容としてセットすることもできます。
            </span>
          </p>
        </div>
        <div className="expandable-container-area">
          <div className="container-title">企業紹介</div>
          <div>
            <Textarea
              name="prompt"
              className="container-textarea"
              value={companyInfo}
              columns={['prompt']}
              isNoErrorMessage={true}
              disabled={true}
            />
          </div>
        </div>
        <div className="description-container-area">
          <div className="description-container">
            <div className="container-title">得意な生産形態</div>
            <div>
              <Textarea
                name="prompt"
                className="container-textarea"
                value={evaluationIndex1}
                columns={['prompt']}
                isNoErrorMessage={true}
                disabled={true}
              />
            </div>
          </div>
          <div className="description-container">
            <div className="container-title">品質保証・検査</div>
            <div>
              <Textarea
                name="prompt"
                className="container-textarea"
                value={evaluationIndex2}
                columns={['prompt']}
                isNoErrorMessage={true}
                disabled={true}
              />
            </div>
          </div>
        </div>
        <div className="description-container-area">
          <div className="description-container">
            <div className="container-title">主要取引業界</div>
            <div>
              <Textarea
                name="prompt"
                className="container-textarea"
                value={evaluationIndex3}
                columns={['prompt']}
                isNoErrorMessage={true}
                disabled={true}
              />
            </div>
          </div>
          <div className="description-container">
            <div className="container-title">技術・設備のポイント</div>
            <div>
              <Textarea
                name="prompt"
                className="container-textarea"
                value={evaluationIndex4}
                columns={['prompt']}
                isNoErrorMessage={true}
                disabled={true}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export const AiAssistEditPanelFooterItem = ({
  onBack: onBack,
  onTempSave: onTempSave,
}: AiAssistEditPanelFooterProps) => {
  const intl = useIntl();
  return (
    <>
      <CaptionButton
        name="resetBtn"
        caption={GetMessageWithIntl(intl, { id: 'return' })}
        className="btn"
        onClick={onBack}
        buttonType="basic"
      />
      <CaptionButton
        name="resetBtn"
        caption={GetMessageWithIntl(intl, {
          prefixId: VIEW_ID,
          id: 'nextTempSave',
        })}
        className="btn"
        onClick={onTempSave}
        buttonType="basic"
      />
    </>
  );
};
